import { HttpErrorResponse } from '@angular/common/http';
import { EStateStatus } from '@app/app.state';
import { IApiError } from '@app/core/interfaces/error.interface';
import { createReducer, on } from '@ngrx/store';
import { EInitEventTypeCode } from '@app/core/enums/init-event-type.enum';
import { ISecurityEventInfo } from '@app/core/interfaces/security-events.interface';
import { EventCardActions } from './event-card.actions';
import {
  IHistReactingProtocolInfo,
  IReactingProtocolStepInfoExtended,
} from '@app/core/interfaces/reacting-protocol.interface';
import {
  IInitEventDisruptionInfo,
  IInitEventDisruptionProtocolGanttInfo,
  IInitEventDisruptionProtocolHeader,
  IInitEventDisruptionPvsTask,
} from 'ngx-mt-ui-lib/api-client';
import { isSameInstant } from './event-card-date.utils';

export interface IEventCardState {
  id: number;
  eventType: EInitEventTypeCode;
  eventInfo: ISecurityEventInfo | IInitEventDisruptionInfo;
  eventPvsTaskList: IInitEventDisruptionPvsTask[];
  activeProtocolInfo: IInitEventDisruptionProtocolHeader;
  activeProtocolSteps: IReactingProtocolStepInfoExtended[];
  activeProtocolStepsPhase: EStateStatus;
  activeProtocolGantt: IInitEventDisruptionProtocolGanttInfo;
  activeProtocolGanttPhase: EStateStatus;
  activeProtocolGanttError: HttpErrorResponse & { error: IApiError };
  histProtocols: IHistReactingProtocolInfo[];
  status: EStateStatus;
  error: HttpErrorResponse & { error: IApiError };
}
export const initialState: IEventCardState = {
  id: null,
  eventType: null,
  eventInfo: null,
  eventPvsTaskList: null,
  activeProtocolInfo: null,
  activeProtocolSteps: [],
  activeProtocolStepsPhase: EStateStatus.PENDING,
  activeProtocolGantt: null,
  activeProtocolGanttPhase: EStateStatus.PENDING,
  activeProtocolGanttError: null,
  histProtocols: [],
  status: EStateStatus.PENDING,
  error: null,
};

export const EventCardReducer = createReducer(
  initialState,
  on(EventCardActions.selectEvent, (state, payload) => ({
    ...state,
    id: payload.id,
    eventType: payload.eventType,
    eventInfo: null,
    eventPvsTaskList: null,
    activeProtocolInfo: null,
    activeProtocolSteps: [],
    activeProtocolStepsPhase: EStateStatus.PENDING,
    activeProtocolGantt: null,
    activeProtocolGanttPhase: EStateStatus.PENDING,
    activeProtocolGanttError: null,
    histProtocols: [],
    status: EStateStatus.PENDING,
  })),
  on(
    EventCardActions.requestEventInfo,
    EventCardActions.requestRefreshEventInfo,
    (state) => ({
      ...state,
      status: EStateStatus.LOADING,
    }),
  ),
  on(
    EventCardActions.requestEventInfoSuccess,
    EventCardActions.requestRefreshEventInfoSuccess,
    (state, payload) => {
      if (payload.eventInfo?.id !== state.id) {
        return state;
      }

      const activeProtocolChanged =
        getActiveProtocolId(state.eventInfo) !==
        getActiveProtocolId(payload.eventInfo);

      return {
        ...state,
        ...(activeProtocolChanged ? getResetActiveProtocolState() : {}),
        eventInfo: payload.eventInfo,
        status: EStateStatus.SUCCESS,
      };
    },
  ),
  on(EventCardActions.requestEventInfoError, (state, payload) => ({
    ...state,
    eventInfo: null,
    activeProtocolInfo: null,
    activeProtocolSteps: [],
    activeProtocolStepsPhase: EStateStatus.PENDING,
    activeProtocolGantt: null,
    activeProtocolGanttPhase: EStateStatus.PENDING,
    activeProtocolGanttError: null,
    histProtocols: [],
    error: payload.error,
    status: EStateStatus.ERROR,
  })),
  on(EventCardActions.requestRefreshEventInfoError, (state, payload) => ({
    ...state,
    error: payload.error,
    status: EStateStatus.ERROR,
  })),
  on(EventCardActions.requestEventPvsTaskList, (state) => ({
    ...state,
    status: EStateStatus.LOADING,
  })),
  on(EventCardActions.requestEventPvsTaskListSuccess, (state, payload) => ({
    ...state,
    eventPvsTaskList: payload.eventPvsTaskList,
    status: EStateStatus.SUCCESS,
  })),
  on(
    EventCardActions.eventCardDisruptionProtocolsSnapshot,
    (state, payload) => {
      if (
        state.id !== payload.disruptionId ||
        state.eventType !== EInitEventTypeCode.NNR
      ) {
        return state;
      }

      const activeProtocolChanged =
        state.activeProtocolInfo?.id !== payload.activeProtocolInfo?.id;

      return {
        ...state,
        ...(activeProtocolChanged ? getResetActiveProtocolState() : {}),
        activeProtocolInfo: payload.activeProtocolInfo,
        activeProtocolSteps: payload.activeProtocolSteps,
        activeProtocolStepsPhase: EStateStatus.SUCCESS,
        histProtocols: payload.histProtocols,
      };
    },
  ),
  on(EventCardActions.requestEventPvsTaskListError, (state, payload) => ({
    ...state,
    eventPvsTaskList: null,
    error: payload.error,
    status: EStateStatus.ERROR,
  })),
  on(EventCardActions.requestEventActiveProtocolGantt, (state, payload) =>
    isCurrentActiveProtocolGanttRequest(state, payload)
      ? {
          ...state,
          activeProtocolGanttPhase: EStateStatus.LOADING,
          activeProtocolGanttError: null,
        }
      : state,
  ),
  on(
    EventCardActions.requestEventActiveProtocolGanttSuccess,
    (state, payload) =>
      isCurrentActiveProtocolGanttRequest(state, payload) &&
      isSameInstant(
        payload.gantt?.launchDate,
        state.activeProtocolInfo?.launchDate,
      )
        ? {
            ...state,
            activeProtocolGantt: payload.gantt,
            activeProtocolGanttPhase: EStateStatus.SUCCESS,
            activeProtocolGanttError: null,
          }
        : state,
  ),
  on(EventCardActions.requestEventActiveProtocolGanttError, (state, payload) =>
    isCurrentActiveProtocolGanttRequest(state, payload)
      ? {
          ...state,
          activeProtocolGanttPhase: EStateStatus.ERROR,
          activeProtocolGanttError: payload.error,
        }
      : state,
  ),
  on(EventCardActions.discardEventActiveProtocolGantt, (state, payload) => {
    if (!isCurrentActiveProtocolGanttRequest(state, payload)) {
      return state;
    }

    const hasCurrentGantt = isSameInstant(
      state.activeProtocolGantt?.launchDate,
      state.activeProtocolInfo?.launchDate,
    );

    return {
      ...state,
      activeProtocolGantt: hasCurrentGantt ? state.activeProtocolGantt : null,
      activeProtocolGanttPhase: hasCurrentGantt
        ? EStateStatus.SUCCESS
        : EStateStatus.PENDING,
      activeProtocolGanttError: null,
    };
  }),
);

function getActiveProtocolId(
  eventInfo: ISecurityEventInfo | IInitEventDisruptionInfo,
): number | null {
  if (eventInfo?.initEventTypeCode !== EInitEventTypeCode.NNR) {
    return null;
  }

  const activeProtocolId = (eventInfo as IInitEventDisruptionInfo)
    .activeProtocolId;

  return typeof activeProtocolId === 'number' && activeProtocolId > 0
    ? activeProtocolId
    : null;
}

function isCurrentActiveProtocol(
  state: IEventCardState,
  protocolId: number,
): boolean {
  return getActiveProtocolId(state.eventInfo) === protocolId;
}

function isCurrentActiveProtocolRequest(
  state: IEventCardState,
  eventId: number,
  protocolId: number,
): boolean {
  return state.id === eventId && isCurrentActiveProtocol(state, protocolId);
}

function isCurrentActiveProtocolGanttRequest(
  state: IEventCardState,
  payload: {
    eventId: number;
    protocolId: number;
    protocolLaunchDate: string;
  },
): boolean {
  return (
    isCurrentActiveProtocolRequest(
      state,
      payload.eventId,
      payload.protocolId,
    ) &&
    isSameInstant(
      payload.protocolLaunchDate,
      state.activeProtocolInfo?.launchDate,
    )
  );
}

function getResetActiveProtocolState(): Pick<
  IEventCardState,
  | 'activeProtocolInfo'
  | 'activeProtocolSteps'
  | 'activeProtocolStepsPhase'
  | 'activeProtocolGantt'
  | 'activeProtocolGanttPhase'
  | 'activeProtocolGanttError'
> {
  return {
    activeProtocolInfo: null,
    activeProtocolSteps: [],
    activeProtocolStepsPhase: EStateStatus.PENDING,
    activeProtocolGantt: null,
    activeProtocolGanttPhase: EStateStatus.PENDING,
    activeProtocolGanttError: null,
  };
}
